import { Suspense, ReactNode, useContext, useEffect, FunctionComponent } from 'react';
import { CircularProgress } from '@mui/material';
import { ErrorBoundary } from 'react-error-boundary';
import { ServicesContext } from 'app/items/shared/context_interface';
import { AgricultureToastType } from 'app/agriculture/AgricultureToast/agriculture_toast_service';

function DefaultLoading() {
    return <CircularProgress />;
}

function DefaultError({ error }: { error: Error }) {
    console.error(error);
    return <>Something went wrong</>;
}

export function ToastError({ error }: { error: Error }) {
    const { agricultureToastService } = useContext(ServicesContext);
    useEffect(() => {
        console.error(error);
        void agricultureToastService.showToast({
            title: 'Something went wrong',
            type: AgricultureToastType.ERROR,
        });
    }, [error, agricultureToastService]);
    return null;
}

interface SuspenseWrapperProps {
    children: ReactNode;
    /**
     * A callback that returns the JSX content to be rendered if the error boundary is hit
     *
     * (e.g. <SuspenseWrapper ErrorComponent={Error}>)
     *
     * (e.g. <SuspenseWrapper ErrorComponent={({error}) => <div>{error}</div>}>)
     *
     * @param error the error object that was thrown
     */
    ErrorComponent?: FunctionComponent<{ error: Error }>;

    /**
     * The alternate JSX content to be rendered while the original content is suspensed
     *
     * (e.g. <SuspenseWrapper loadingComponent={<LoadingSpinner />}>)
     *
     * Note: if this loadingComponent is suspensed, it will trigger the next SuspenseWrapper up the tree
     */
    loadingComponent?: ReactNode;
}

export function SuspenseWrapper({
    children,
    loadingComponent = <DefaultLoading />,
    ErrorComponent = DefaultError,
}: SuspenseWrapperProps) {
    return (
        <Suspense fallback={loadingComponent}>
            <ErrorBoundary FallbackComponent={ErrorComponent}>{children}</ErrorBoundary>
        </Suspense>
    );
}
